import type { SubjectType } from "../types/property";

/**
 * Mode-aware UI configuration derived from a property's `subjectType`.
 *
 * Used to gate sections of the dashboard, sidebar, settings, and report
 * viewer that don't apply to every subject. The dividing line is a capability,
 * not a rank: robots.txt analysis, sitemap import, AI bot traffic and the site
 * audit all assume a crawlable root domain, so they are hidden for subjects
 * that don't have one (PERSON, CATEGORY, PRODUCT, LOCATION) — while the
 * PR/market-position surfaces are open to every subject that has competitors
 * and earned coverage, which includes PRODUCT and CATEGORY.
 *
 * Every multi-subject flag is written as an explicit allowlist
 * (`isBrand || isProduct`), never as a negation (`!isLocation`): with a
 * negation, a new type joining the `SubjectType` enum is admitted to the set by
 * accident, and nobody finds out until the surface renders empty for it.
 *
 * Pattern mirrors the existing persona-based conditional rendering in
 * PropertySidebar.tsx — define a configuration object once and consume it
 * in components rather than scattering inline ternaries on subjectType.
 */
/** Project types a property can create — mirrors the Prisma `ProjectType` enum. */
export type PropertyProjectType = "SEO" | "PR" | "GENERAL";

export interface SubjectModeConfig {
	readonly subjectType: SubjectType;
	/** True for BRAND subjects only (those with a crawlable root domain). */
	readonly isBrand: boolean;
	/** True for LOCATION sub-properties that inherit context from a BRAND parent. */
	readonly isLocation: boolean;
	/**
	 * True for PRODUCT subjects — a named product/SKU tracked in its own right.
	 * A product has a market position (competitors, share of voice, PR, its own
	 * SERP grounding gaps) but no crawlable root domain of its own: its pages
	 * live on the parent company's site, so the crawl-derived surfaces
	 * (robots.txt, sitemap import, site audit, AI traffic, FAQ generation) stay
	 * off. Flags that mean "has a market position" therefore read
	 * `isBrand || isProduct`; flags that mean "owns a site" stay `isBrand`.
	 */
	readonly isProduct: boolean;
	/**
	 * True for CATEGORY subjects only — a market tracked with no focal brand
	 * ("project management tools", "energy providers for AI data centers").
	 */
	readonly isCategory: boolean;
	/**
	 * True when the property tracks a named subject whose own share of voice is
	 * meaningful: BRAND, PERSON, PRODUCT and LOCATION. False for CATEGORY, which
	 * is the market itself — there is no "you" row in the competitor table, no
	 * hero share-of-voice number, and no vs-you gap to compute. Written as an
	 * allowlist rather than `!isCategory` so a new subject type has to be
	 * admitted here deliberately.
	 */
	readonly hasFocalSubject: boolean;
	/**
	 * PowerPoint export of the AI Visibility Report. The deck is built around a
	 * focal subject (its snapshot, its share of voice, its gaps), so it is
	 * offered only where a deck template exists for the subject. A category has
	 * a PDF template and no deck.
	 */
	readonly showPptExport: boolean;
	/** Robots.txt analysis section in reports + property settings. */
	readonly showRobotsTxt: boolean;
	/** Sitemap import job + Pages section in the sidebar/manage view. */
	readonly showSitemapImport: boolean;
	/** Site Audit report + AI Readiness Audit section. */
	readonly showSiteAudit: boolean;
	/** AI Analytics, Bot Traffic, Traffic Control, Integrations sections. */
	readonly showAnalytics: boolean;
	/**
	 * Competitors section — every subject type. The competitor set is the point
	 * of every subject, and for a CATEGORY it is the whole dashboard: with no
	 * focal brand, the breakout of who AI names in the market is all there is.
	 */
	readonly showCompetitors: boolean;
	/** Brand snapshot editor and key messages sections (available for all subject types). */
	readonly showSnapshotEditor: boolean;
	readonly showKeyMessages: boolean;
	/** Whether the snapshot can be edited at this property (false for LOCATION — parent owns it). */
	readonly snapshotEditable: boolean;
	/** Whether key messages can be edited at this property (false for LOCATION — parent owns them). */
	readonly keyMessagesEditable: boolean;
	/**
	 * Grounding-search SERP gap analysis — BRAND and PRODUCT. Needs a focal
	 * domain to rank against, which a product borrows from its parent company's
	 * site (product pages rank there). PERSON is excluded: identity prompts
	 * don't emit grounding searches at all.
	 */
	readonly showGroundingSearches: boolean;
	/** Identity-accuracy report sections (PERSON only). */
	readonly showIdentityAnalysis: boolean;
	/**
	 * Brand Consistency dashboard module + its drill-down — BRAND only.
	 * PERSON gets Identity Accuracy in that slot instead (same stored checks,
	 * different framing). PRODUCT gets neither: no job computes consistency
	 * checks for products, so the card would be permanently blank.
	 */
	readonly showConsistency: boolean;
	/** Tracked-attribute (positive/negative narrative) sections. */
	readonly showTrackedAttributes: boolean;
	/** Peer-comparison section — PERSON's relabeled competitor comparison. */
	readonly showPeerComparison: boolean;
	/** Content section (FAQ Generator) — BRAND only; needs a crawlable site. */
	readonly showContent: boolean;
	/**
	 * Projects section — BRAND, PERSON, PRODUCT and CATEGORY. Projects (and their prompts
	 * / goals) are owned by the parent property; a LOCATION inherits the
	 * parent's prompts at run time and cannot own its own projects. Nightly runs
	 * fan a parent project's prompts out to every location automatically, so
	 * per-location projects would be orphaned (empty prompt picker, never
	 * dispatched). PERSON projects power nightly tracking for
	 * spokesperson/executive visibility (peers, identity accuracy, key-message
	 * trends); PRODUCT projects track a launch or a category push; a CATEGORY
	 * project tracks who AI names in a market, daily.
	 */
	readonly showProjects: boolean;
	/**
	 * Project types this subject can create. SEO projects track a page group on
	 * the property's own site, so they need a crawlable domain (BRAND only);
	 * PR projects track earned placements, which individuals and products have
	 * too (a law firm's PR is often partner-level; a product launch is a PR
	 * campaign; a category's coverage is the trade press that shapes it). The
	 * service enforces the same rule server-side — see
	 * `packages/core/src/services/project.ts`.
	 */
	readonly projectTypes: readonly PropertyProjectType[];
	/** Coverage groups (bulk earned-media placement lists) — PR feature, BRAND + PERSON + PRODUCT + CATEGORY. */
	readonly showCoverageGroups: boolean;
	/**
	 * Placements section — PR feature, BRAND + PERSON + PRODUCT + CATEGORY
	 * (partner-, product- and market-level PR work).
	 */
	readonly showPlacements: boolean;
	/**
	 * Earned-media opportunities (the dashboard module and its L2). Needs a
	 * competitor/peer set whose coverage can be compared against the subject's
	 * own — BRAND, PERSON and PRODUCT. CATEGORY is excluded even though the rest
	 * of the PR surfaces are on for it: the metric is a gap against the
	 * subject's own coverage baseline, and a CATEGORY has no subject to
	 * baseline. Deliberately separate from
	 * `showPlacements`: that one gates the PR workspace, this one gates a
	 * citation-derived metric, and they are free to diverge.
	 */
	readonly showEarnedMedia: boolean;
	/**
	 * The category Publishers view (the dashboard module and its L2): which
	 * publishers AI cites for the market's prompts and which tracked brands
	 * those publishers carry. CATEGORY only. It is the no-focal-subject
	 * counterpart of `showEarnedMedia`: the same citation sources, read without
	 * a "you" to measure a gap against, so the two flags never overlap.
	 */
	readonly showCategoryPublishers: boolean;
	/** Publisher Lookup — PR feature, BRAND + PERSON + PRODUCT + CATEGORY. */
	readonly showPublisherLookup: boolean;
	/** Whether `Property.domain` is required and meaningful. */
	readonly requiresDomain: boolean;
	/** Whether this subject must have a parentId (LOCATION-only). */
	readonly parentPropertyRequired: boolean;
	/** Whether this subject can spawn LOCATION children (BRAND + PRODUCT + CATEGORY). */
	readonly canHaveLocations: boolean;
	/** Human-readable label for the snapshot card heading. */
	readonly snapshotLabel: string;
	/** Human-readable label for the subject in copy ("brand", "person", ...). */
	readonly subjectLabel: string;
	/** Label for the competitor/peer set ("Competitors" vs "Peers"). */
	readonly peerLabel: string;
}

export function subjectModeConfig(
	subjectType: SubjectType | null | undefined,
): SubjectModeConfig {
	const type: SubjectType = subjectType ?? "BRAND";
	const isBrand = type === "BRAND";
	const isLocation = type === "LOCATION";
	const isPerson = type === "PERSON";
	const isProduct = type === "PRODUCT";
	const isCategory = type === "CATEGORY";
	// Every multi-subject gate below is an explicit allowlist, never `!isLocation`
	// or `!isPerson`, so that a new type joining the `SubjectType` enum is never
	// admitted to one of these sets by accident.
	return {
		subjectType: type,
		isBrand,
		isLocation,
		isProduct,
		isCategory,
		hasFocalSubject: isBrand || isPerson || isProduct || isLocation,
		showPptExport: isBrand || isPerson || isProduct || isLocation,
		// LOCATION children inherit the parent BRAND's domain and crawl-derived
		// data. Anything that would re-crawl, re-audit, or re-edit ground truth
		// is hidden on a LOCATION; the child only owns its location, its
		// competitors, and its accrued metrics.
		showRobotsTxt: isBrand,
		showSitemapImport: isBrand,
		showSiteAudit: isBrand,
		showAnalytics: isBrand,
		// The competitor set is the point of every subject; for a CATEGORY the
		// breakout is the entire dashboard.
		showCompetitors: true,
		showSnapshotEditor: true,
		showKeyMessages: true,
		snapshotEditable: !isLocation,
		keyMessagesEditable: !isLocation,
		showGroundingSearches: isBrand || isProduct,
		showIdentityAnalysis: isPerson,
		showConsistency: isBrand,
		showTrackedAttributes: true,
		showPeerComparison: isPerson,
		showContent: isBrand,
		showProjects: isBrand || isPerson || isProduct || isCategory,
		projectTypes: isBrand
			? (["SEO", "PR", "GENERAL"] as const)
			: isPerson || isProduct || isCategory
				? (["PR", "GENERAL"] as const)
				: [],
		showCoverageGroups: isBrand || isPerson || isProduct || isCategory,
		showPlacements: isBrand || isPerson || isProduct || isCategory,
		// No CATEGORY: this one needs the subject's own coverage baseline to
		// measure a gap against, and a CATEGORY has no subject.
		showEarnedMedia: isBrand || isPerson || isProduct,
		// The gap-free counterpart for the one subject without a baseline.
		showCategoryPublishers: isCategory,
		showPublisherLookup: isBrand || isPerson || isProduct || isCategory,
		requiresDomain: isBrand,
		parentPropertyRequired: isLocation,
		canHaveLocations: isBrand || isProduct || isCategory,
		snapshotLabel:
			type === "BRAND"
				? "Brand snapshot"
				: type === "PERSON"
					? "Profile snapshot"
					: type === "CATEGORY"
						? "Category snapshot"
						: type === "LOCATION"
							? "Brand snapshot (inherited)"
							: "Product snapshot",
		subjectLabel:
			type === "BRAND"
				? "brand"
				: type === "PERSON"
					? "person"
					: type === "CATEGORY"
						? "category"
						: type === "LOCATION"
							? "location"
							: "product",
		peerLabel: type === "PERSON" ? "Peers" : "Competitors",
	};
}
